/**
 * 契約情報フォームのセクション（3ブロック）。
 * 雇用形態の Step 2 / 従業員の Step 2 から共通で利用する。
 *
 * 大項目:
 *   1. 必須情報（一日の所定労働時間 / 週の休みの日数 / 祝日を休みとするか）
 *   2. 月ごとの公休日数と勤務時間（12ヶ月分の表）
 *   3. 曜日ごとの勤務形態（7曜日 + 任意の祝日行）
 *
 * 入力値は親コンポーネントが管理する controlled component。
 */
import { useState } from "react";
import clsx from "clsx";
import { Button } from "./atoms";

export interface MonthlyCondition {
    month: number;
    publicHolidaysMin: number;
    publicHolidaysMax: number;
    workHoursMinHours: number;
    workHoursMinMinutes: number;
    workHoursMaxHours: number;
    workHoursMaxMinutes: number;
}

export interface WeekdayRule {
    dayOfWeek: string;
    attendanceType: "必ず出勤" | "必要に応じて出勤" | "休み";
    workStartTime: string;
    workEndTime: string;
    scheduleType: "固定（毎週このルール）" | "可変（この範囲内で調整）";
}

export interface ContractInfoFormValue {
    /** 一日の所定労働時間 - 時間 */
    prescribedHours: number | "";
    /** 一日の所定労働時間 - 分 */
    prescribedMinutes: number | "";
    /** 週の休みの日数（0〜7） */
    weeklyDaysOff: number | "";
    /** 祝日を休みとするか */
    isHolidayOff: boolean;
    /** 月ごとの公休日数と勤務時間（12ヶ月分） */
    monthlyConditions: MonthlyCondition[];
    /** 曜日ごとの勤務形態（7日分） */
    weekdayRules: WeekdayRule[];
    /** 祝日専用の設定をするか */
    hasHolidaySettings: boolean;
    /** 祝日の勤務形態（hasHolidaySettings = true のときのみ有効） */
    holidayRule: WeekdayRule | null;
}

const WEEKDAY_LABEL_LIST: string[] = ["月", "火", "水", "木", "金", "土", "日"];

const generateEmptyMonthlyConditions = (): MonthlyCondition[] =>
    Array.from({ length: 12 }, (_, i) => ({
        month: i + 1,
        publicHolidaysMin: 0,
        publicHolidaysMax: 0,
        workHoursMinHours: 0,
        workHoursMinMinutes: 0,
        workHoursMaxHours: 0,
        workHoursMaxMinutes: 0,
    }));

const generateDefaultWeekdayRules = (): WeekdayRule[] =>
    WEEKDAY_LABEL_LIST.map((day) => ({
        dayOfWeek: day,
        attendanceType: "必ず出勤",
        workStartTime: "09:00",
        workEndTime: "18:00",
        scheduleType: "固定（毎週このルール）",
    }));

export const EMPTY_CONTRACT_INFO_FORM_VALUE: ContractInfoFormValue = {
    prescribedHours: "",
    prescribedMinutes: "",
    weeklyDaysOff: "",
    isHolidayOff: true,
    monthlyConditions: generateEmptyMonthlyConditions(),
    weekdayRules: generateDefaultWeekdayRules(),
    hasHolidaySettings: false,
    holidayRule: null,
};

const PRESCRIBED_TIME_LIMIT_MINUTES = 24 * 60;

/** 時間選択肢を生成（00:00〜23:59、30分刻み + 23:59） */
const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 30) {
            times.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
    }
    times.push("23:59");
    return times;
};

const TIME_OPTIONS = generateTimeOptions();

const ATTENDANCE_TYPE_OPTIONS = [
    { value: "必ず出勤", label: "必ず出勤" },
    { value: "必要に応じて出勤", label: "必要に応じて出勤" },
    { value: "休み", label: "休み" },
] as const;

const SCHEDULE_TYPE_OPTIONS = [
    { value: "固定（毎週このルール）", label: "固定（毎週このルール）" },
    { value: "可変（この範囲内で調整）", label: "可変（この範囲内で調整）" },
] as const;

const cellSelectClass =
    "w-full bg-transparent border-0 text-xs text-gray-600 focus:outline-none cursor-pointer";

const numberValueOrEmpty = (raw: string): number | "" => {
    if (raw === "") {return "";}
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? "" : parsed;
};

const totalMinutes = (hours: number | "", minutes: number | ""): number => {
    const h = typeof hours === "number" ? hours : 0;
    const m = typeof minutes === "number" ? minutes : 0;
    return h * 60 + m;
};

const SectionHeader = ({ title }: { title: string }) => (
    <h2 className="text-base font-bold text-gray-800">{title}</h2>
);

const RowLabel = ({ children }: { children: React.ReactNode }) => (
    <label className="w-44 flex-shrink-0 text-sm text-gray-700 pt-2">
        {children}
    </label>
);

interface WeekdayRuleRowProps {
    rule: WeekdayRule;
    onChange: (field: keyof WeekdayRule, value: string) => void;
}

const WeekdayRuleRow = ({ rule, onChange }: WeekdayRuleRowProps) => {
    const getValidEndTimes = (): string[] => {
        const startIndex = TIME_OPTIONS.indexOf(rule.workStartTime);
        return TIME_OPTIONS.filter((_, i) => i > startIndex);
    };

    return (
        <tr className="border-t border-gray-200">
            <td className="px-4 py-3 text-center text-gray-600 border-r border-gray-200 bg-[#E9F1F2]">
                {rule.dayOfWeek}
            </td>
            <td className="px-3 py-3 border-r border-gray-200">
                <select
                    value={rule.attendanceType}
                    onChange={(e) => onChange("attendanceType", e.target.value)}
                    className={cellSelectClass}
                >
                    {ATTENDANCE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </td>
            <td className="px-4 py-3 border-r border-gray-200">
                <select
                    value={rule.workStartTime}
                    onChange={(e) => onChange("workStartTime", e.target.value)}
                    className={cellSelectClass}
                    disabled={rule.attendanceType === "休み"}
                >
                    {TIME_OPTIONS.map((time) => (
                        <option key={time} value={time}>
                            {time}
                        </option>
                    ))}
                </select>
            </td>
            <td className="px-4 py-3 border-r border-gray-200">
                <select
                    value={rule.workEndTime}
                    onChange={(e) => onChange("workEndTime", e.target.value)}
                    className={cellSelectClass}
                    disabled={rule.attendanceType === "休み"}
                >
                    {getValidEndTimes().map((time) => (
                        <option key={time} value={time}>
                            {time}
                        </option>
                    ))}
                </select>
            </td>
            <td className="px-3 py-3">
                <select
                    value={rule.scheduleType}
                    onChange={(e) => onChange("scheduleType", e.target.value)}
                    className={cellSelectClass}
                    disabled={rule.attendanceType === "休み"}
                >
                    {SCHEDULE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </td>
        </tr>
    );
};

type CalculateMissingField = "prescribedTime" | "weeklyDaysOff";

export interface ContractInfoFormSectionProps {
    value: ContractInfoFormValue;
    onChange: (next: ContractInfoFormValue) => void;
    /** 「曜日ごとの勤務形態」セクションを表示するか（デフォルト true） */
    showWeekdayRules?: boolean;
}

export const ContractInfoFormSection = ({
    value,
    onChange,
    showWeekdayRules = true,
}: ContractInfoFormSectionProps) => {
    const [missingFieldList, setMissingFieldList] = useState<CalculateMissingField[]>([]);

    const update = (partial: Partial<ContractInfoFormValue>) => {
        onChange({ ...value, ...partial });
    };

    const handlePrescribedTimeChange = (
        field: "prescribedHours" | "prescribedMinutes",
        raw: string,
    ) => {
        update({ [field]: numberValueOrEmpty(raw) });
        setMissingFieldList((prev) => prev.filter((f) => f !== "prescribedTime"));
    };

    const handleWeeklyDaysOffChange = (raw: string) => {
        update({ weeklyDaysOff: numberValueOrEmpty(raw) });
        setMissingFieldList((prev) => prev.filter((f) => f !== "weeklyDaysOff"));
    };

    const handleIsHolidayOffChange = (checked: boolean) => {
        update({ isHolidayOff: checked });
    };

    const handleMonthlyConditionChange = (
        monthIndex: number,
        field: keyof MonthlyCondition,
        newValue: number,
    ) => {
        const next = [...value.monthlyConditions];
        next[monthIndex] = { ...next[monthIndex], [field]: newValue };
        update({ monthlyConditions: next });
    };

    const handleWeekdayRuleChange = (
        dayIndex: number,
        field: keyof WeekdayRule,
        newValue: string,
    ) => {
        const next = [...value.weekdayRules];
        next[dayIndex] = { ...next[dayIndex], [field]: newValue };
        update({ weekdayRules: next });
    };

    const handleHolidaySettingsToggle = (checked: boolean) => {
        update({
            hasHolidaySettings: checked,
            holidayRule: checked
                ? value.holidayRule ?? {
                      dayOfWeek: "祝",
                      attendanceType: "休み",
                      workStartTime: "09:00",
                      workEndTime: "18:00",
                      scheduleType: "固定（毎週このルール）",
                  }
                : null,
        });
    };

    const handleHolidayRuleChange = (field: keyof WeekdayRule, newValue: string) => {
        if (!value.holidayRule) {return;}
        update({ holidayRule: { ...value.holidayRule, [field]: newValue } });
    };

    const isMinutesOutOfRange =
        typeof value.prescribedMinutes === "number" && value.prescribedMinutes >= 60;
    const isPrescribedTimeOver24h =
        totalMinutes(value.prescribedHours, value.prescribedMinutes) >
        PRESCRIBED_TIME_LIMIT_MINUTES;

    const handleCalculate = () => {
        const missing: CalculateMissingField[] = [];
        const isPrescribedTimeEntered =
            typeof value.prescribedHours === "number" ||
            typeof value.prescribedMinutes === "number";
        if (!isPrescribedTimeEntered) {
            missing.push("prescribedTime");
        }
        if (value.weeklyDaysOff === "") {
            missing.push("weeklyDaysOff");
        }
        setMissingFieldList(missing);
        if (missing.length > 0) {return;}
        // TODO: 後続タスクで「月の契約」の自動計算ロジックを実装する
    };

    const isPrescribedTimeMissing = missingFieldList.includes("prescribedTime");
    const isWeeklyDaysOffMissing = missingFieldList.includes("weeklyDaysOff");

    return (
        <div className="space-y-10">
            {/* 1. 必須情報 */}
            <section className="space-y-6">
                <SectionHeader title="必須情報" />

                {/* 一日の所定労働時間 */}
                <div className="flex items-start gap-6">
                    <RowLabel>一日の所定労働時間</RowLabel>
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                inputMode="numeric"
                                value={value.prescribedHours}
                                onChange={(e) =>
                                    handlePrescribedTimeChange("prescribedHours", e.target.value)
                                }
                                className={clsx(
                                    "w-20 px-3 py-2 border rounded text-sm text-center",
                                    isPrescribedTimeOver24h || isPrescribedTimeMissing
                                        ? "border-red-500"
                                        : "border-gray-300",
                                )}
                            />
                            <span className="text-sm text-gray-700">時間</span>
                            <input
                                type="text"
                                inputMode="numeric"
                                value={value.prescribedMinutes}
                                onChange={(e) =>
                                    handlePrescribedTimeChange("prescribedMinutes", e.target.value)
                                }
                                className={clsx(
                                    "w-20 px-3 py-2 border rounded text-sm text-center",
                                    isPrescribedTimeOver24h
                                        || isMinutesOutOfRange
                                        || isPrescribedTimeMissing
                                        ? "border-red-500"
                                        : "border-gray-300",
                                )}
                            />
                            <span className="text-sm text-gray-700">分</span>
                        </div>
                        {isMinutesOutOfRange && (
                            <p className="mt-2 text-sm text-red-600">
                                分は60未満で入力してください。
                            </p>
                        )}
                        {isPrescribedTimeOver24h && (
                            <p className="mt-2 text-sm text-red-600">
                                24時間00分を超えて設定することはできません。
                            </p>
                        )}
                        {isPrescribedTimeMissing && (
                            <p className="mt-2 text-sm text-red-600">
                                一日の所定労働時間を入力してください。
                            </p>
                        )}
                    </div>
                </div>

                {/* 週の休みの日数 */}
                <div className="flex items-start gap-6">
                    <RowLabel>週の休みの日数</RowLabel>
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <select
                                value={
                                    value.weeklyDaysOff === ""
                                        ? ""
                                        : String(value.weeklyDaysOff)
                                }
                                onChange={(e) => handleWeeklyDaysOffChange(e.target.value)}
                                className={clsx(
                                    "w-24 px-3 py-2 border rounded text-sm bg-white",
                                    isWeeklyDaysOffMissing
                                        ? "border-red-500"
                                        : "border-gray-300",
                                )}
                            >
                                <option value="">未選択</option>
                                {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>
                            <span className="text-sm text-gray-700">日</span>
                        </div>
                        {isWeeklyDaysOffMissing && (
                            <p className="mt-2 text-sm text-red-600">
                                週の休みの日数を選択してください。
                            </p>
                        )}
                    </div>
                </div>

                {/* 祝日を休みとするか */}
                <div className="flex items-start gap-6">
                    <RowLabel>祝日を休みとするか</RowLabel>
                    <div className="flex-1 pt-2">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={value.isHolidayOff}
                                onChange={(e) => handleIsHolidayOffChange(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                            />
                            <span className="text-sm text-gray-700">祝日を休みとする</span>
                        </label>
                    </div>
                </div>

                {/* 月の契約を算出するボタン */}
                <div className="flex items-start gap-6">
                    <RowLabel>{""}</RowLabel>
                    <div className="flex-1">
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={handleCalculate}
                        >
                            この値をもとに月の契約を算出する
                        </Button>
                    </div>
                </div>
            </section>

            {/* 2. 月ごとの公休日数と勤務時間 */}
            <section className="space-y-4">
                <SectionHeader title="月ごとの公休日数と勤務時間" />
                <div className="overflow-x-auto max-w-[1200px] scrollbar-table">
                    <div className="border border-gray-200 rounded">
                        <table className="text-xs border-collapse">
                            <thead>
                                <tr className="bg-[#E9F1F2]">
                                    <th className="px-3 py-2.5 text-center font-normal text-gray-600 border-r border-gray-200 bg-[#E9F1F2] w-28 sticky left-0 z-10" />
                                    {value.monthlyConditions.map((condition) => (
                                        <th
                                            key={condition.month}
                                            className="px-4 py-2.5 text-center font-normal text-gray-600 border-r border-gray-200 bg-[#E9F1F2] last:border-r-0 min-w-[145px]"
                                        >
                                            {condition.month}月
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* 公休日数 上限 */}
                                <tr className="border-t border-gray-200">
                                    <td className="px-3 py-3 text-center text-gray-600 border-r border-gray-200 bg-[#E9F1F2] whitespace-nowrap sticky left-0 z-10">
                                        公休日数 上限
                                    </td>
                                    {value.monthlyConditions.map((condition, index) => (
                                        <td key={condition.month} className="px-2 py-3 border-r border-gray-200 last:border-r-0 min-w-[145px]">
                                            <div className="flex items-center justify-center gap-1">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={31}
                                                    value={condition.publicHolidaysMax || ""}
                                                    onChange={(e) =>
                                                        handleMonthlyConditionChange(
                                                            index,
                                                            "publicHolidaysMax",
                                                            Number(e.target.value) || 0,
                                                        )
                                                    }
                                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                                                />
                                                <span className="text-xs text-gray-600">日</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                                {/* 公休日数 下限 */}
                                <tr className="border-t border-gray-200">
                                    <td className="px-3 py-3 text-center text-gray-600 border-r border-gray-200 bg-[#E9F1F2] whitespace-nowrap sticky left-0 z-10">
                                        公休日数 下限
                                    </td>
                                    {value.monthlyConditions.map((condition, index) => (
                                        <td key={condition.month} className="px-2 py-3 border-r border-gray-200 last:border-r-0 min-w-[145px]">
                                            <div className="flex items-center justify-center gap-1">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={31}
                                                    value={condition.publicHolidaysMin || ""}
                                                    onChange={(e) =>
                                                        handleMonthlyConditionChange(
                                                            index,
                                                            "publicHolidaysMin",
                                                            Number(e.target.value) || 0,
                                                        )
                                                    }
                                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                                                />
                                                <span className="text-xs text-gray-600">日</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                                {/* 勤務時間 上限 */}
                                <tr className="border-t border-gray-200">
                                    <td className="px-3 py-3 text-center text-gray-600 border-r border-gray-200 bg-[#E9F1F2] whitespace-nowrap sticky left-0 z-10">
                                        勤務時間 上限
                                    </td>
                                    {value.monthlyConditions.map((condition, index) => (
                                        <td key={condition.month} className="px-2 py-3 border-r border-gray-200 last:border-r-0 min-w-[145px]">
                                            <div className="flex items-center justify-center gap-0.5">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={999}
                                                    value={condition.workHoursMaxHours || ""}
                                                    onChange={(e) =>
                                                        handleMonthlyConditionChange(
                                                            index,
                                                            "workHoursMaxHours",
                                                            Number(e.target.value) || 0,
                                                        )
                                                    }
                                                    className="w-14 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                                                />
                                                <span className="text-xs text-gray-600">h</span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={59}
                                                    value={condition.workHoursMaxMinutes || ""}
                                                    onChange={(e) =>
                                                        handleMonthlyConditionChange(
                                                            index,
                                                            "workHoursMaxMinutes",
                                                            Number(e.target.value) || 0,
                                                        )
                                                    }
                                                    className="w-10 px-1 py-1 border border-gray-300 rounded text-xs text-center"
                                                />
                                                <span className="text-xs text-gray-600">m</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                                {/* 勤務時間 下限 */}
                                <tr className="border-t border-gray-200">
                                    <td className="px-3 py-3 text-center text-gray-600 border-r border-gray-200 bg-[#E9F1F2] whitespace-nowrap sticky left-0 z-10">
                                        勤務時間 下限
                                    </td>
                                    {value.monthlyConditions.map((condition, index) => (
                                        <td key={condition.month} className="px-2 py-3 border-r border-gray-200 last:border-r-0 min-w-[145px]">
                                            <div className="flex items-center justify-center gap-0.5">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={999}
                                                    value={condition.workHoursMinHours || ""}
                                                    onChange={(e) =>
                                                        handleMonthlyConditionChange(
                                                            index,
                                                            "workHoursMinHours",
                                                            Number(e.target.value) || 0,
                                                        )
                                                    }
                                                    className="w-14 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                                                />
                                                <span className="text-xs text-gray-600">h</span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={59}
                                                    value={condition.workHoursMinMinutes || ""}
                                                    onChange={(e) =>
                                                        handleMonthlyConditionChange(
                                                            index,
                                                            "workHoursMinMinutes",
                                                            Number(e.target.value) || 0,
                                                        )
                                                    }
                                                    className="w-10 px-1 py-1 border border-gray-300 rounded text-xs text-center"
                                                />
                                                <span className="text-xs text-gray-600">m</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* 3. 曜日ごとの勤務形態 */}
            {showWeekdayRules && (
            <section className="space-y-4">
                <SectionHeader title="曜日ごとの勤務形態" />
                <div>
                    <div className="border border-gray-200 rounded inline-block">
                        <table className="text-xs border-collapse">
                            <thead>
                                <tr className="bg-[#E9F1F2]">
                                    <th className="px-4 py-2.5 text-center font-normal text-gray-600 border-r border-gray-200">
                                        曜日
                                    </th>
                                    <th className="px-4 py-2.5 text-center font-normal text-gray-600 border-r border-gray-200">
                                        出勤区分
                                    </th>
                                    <th className="px-6 py-2.5 text-center font-normal text-gray-600 border-r border-gray-200">
                                        勤務可能時間帯(開始)
                                    </th>
                                    <th className="px-6 py-2.5 text-center font-normal text-gray-600 border-r border-gray-200">
                                        勤務可能時間帯(終了)
                                    </th>
                                    <th className="px-4 py-2.5 text-center font-normal text-gray-600">
                                        固定/可変
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {value.weekdayRules.map((rule, index) => (
                                    <WeekdayRuleRow
                                        key={rule.dayOfWeek}
                                        rule={rule}
                                        onChange={(field, newValue) =>
                                            handleWeekdayRuleChange(index, field, newValue)
                                        }
                                    />
                                ))}
                                {value.hasHolidaySettings && value.holidayRule && (
                                    <WeekdayRuleRow
                                        rule={value.holidayRule}
                                        onChange={(field, newValue) =>
                                            handleHolidayRuleChange(field, newValue)
                                        }
                                    />
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 祝日専用設定チェックボックス */}
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={value.hasHolidaySettings}
                            onChange={(e) => handleHolidaySettingsToggle(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                        />
                        <span className="text-sm text-gray-700">
                            祝日専用の設定をする
                        </span>
                    </label>
                </div>
            </section>
            )}
        </div>
    );
};

export default ContractInfoFormSection;
